/**
 * Optimizer mode registry — the single source of truth for every mode-facing
 * string, type and routing decision.
 *
 * Lives in `types/` rather than in SuggestionContext so that the service layer
 * (`services/typedApiClient`) and hooks (`hooks/useOptimizerActions`) can use
 * the type without importing from a React context — SuggestionContext already
 * imports from useOptimizerActions, so the reverse direction would be a cycle.
 * SuggestionContext re-exports everything below for its existing consumers.
 */

/**
 * `v2` (CP-SAT) and `v3` (hybrid) were deleted on 2026-08-23, following `v5`
 * on 2026-08-06. The decision was measured, not assumed: across the entire
 * lifetime of `optimizer_dispatch_events`, production requested `v4` and
 * nothing else, and the only way to request anything else was the unpersisted
 * dev cycle below. The backend now rejects them with a 400.
 *
 * What remains is the production engine and the greedy fallback the dispatcher
 * drops to when it fails — which is the whole set of things that can actually
 * serve a plan.
 */
export const OPTIMIZER_MODES = ['v1', 'v4'] as const;
export type OptimizerMode = (typeof OPTIMIZER_MODES)[number];

/**
 * Modes whose "Suggest Courses" button routes through the chat assistant
 * rather than firing a direct generate.
 */
export const CHATBOT_ROUTED_MODES: readonly OptimizerMode[] = ['v4'];

/**
 * Cmd+Shift+O cycle order. An ephemeral dev affordance, not an A/B mechanism —
 * a real experiment needs sticky per-user arms assigned server-side. The mode
 * is never persisted, so it resets to the default on every page load.
 *
 * Admin-gated since 2026-08-23: it had been reachable by any logged-in user,
 * which meant a student could put themselves on the non-default engine for a
 * page session. Now a two-element cycle between the production engine and its
 * fallback.
 */
export const OPTIMIZER_ROTATION: Record<OptimizerMode, OptimizerMode> = {
  v4: 'v1', v1: 'v4',
};

/** The production default. */
export const DEFAULT_OPTIMIZER_MODE: OptimizerMode = 'v4';

/** Short human names, single source of truth for every mode-facing string. */
export const OPTIMIZER_LABELS: Record<OptimizerMode, string> = {
  v1: 'Greedy',
  v4: 'Priority',
};
